import { useState, useMemo } from 'react';
import { fmt, fmtUSD, computeDerived, buildForecast, milestonesFor, milestoneAt, ageAt, CLASS_COLOR } from '../lib/portfolio';
import { memberLabel } from '../lib/theme';
import { glassCard, pageBg, cell, th, labelCell, sectionTitle, sectionSub, editInput } from '../lib/ui';
import PlanReminder from '../components/PlanReminder';
import { apiSend } from '../lib/api';
import { Save, Check } from 'lucide-react';

export default function Planning({ data, savedPlan, savedAt, members, fcPrefs = {}, saveFcPrefs, onSaved }) {
  // Initialise from the SAVED plan when one exists (so it survives tab switches);
  // otherwise seed from current actuals.
  const [planned, setPlanned] = useState(() =>
    (savedPlan?.planned && Object.keys(savedPlan.planned).length)
      ? JSON.parse(JSON.stringify(savedPlan.planned))
      : JSON.parse(JSON.stringify(data?.matrix || {})));
  const [cagrs, setCagrs] = useState(() => {
    const c = {}; (data?.asset_meta || []).forEach(m => { c[m.asset] = m.cagr; });
    return (savedPlan?.cagrs && Object.keys(savedPlan.cagrs).length) ? { ...c, ...savedPlan.cagrs } : c;
  });
  // per-user default monthly investment (Pranav ₹2L, Golu ₹1L per their sheets)
  const defaultMonthly = data?.user_id === 'golu' ? 100000 : 200000;
  const [monthlyBase, setMonthlyBase] = useState(savedPlan?.monthlyBase || defaultMonthly);
  const [monthlySchedule, setMonthlySchedule] = useState(savedPlan?.monthlySchedule || {});
  const [member, setMember] = useState('ALL');
  const [overrides, setOverrides] = useState(savedPlan?.overrides || {});
  const [flows, setFlows] = useState(savedPlan?.flows || {});
  const [edit, setEdit] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [saveState, setSaveState] = useState('idle'); // idle|saving|saved
  // 3 year-selectable forecast cards, default to the age 40/50/60 years
  const [cardYears, setCardYears] = useState(() => {
    if (Array.isArray(fcPrefs.planYears) && fcPrefs.planYears.length === 3) return fcPrefs.planYears;
    const ms = milestonesFor(data?.dob);
    return ms.length ? ms.map(m => m.year) : [2031, 2041, 2051];
  });

  const meta = data?.asset_meta || [];
  const entities = data?.entity_order || ['PRANAV', 'RASHU', 'HUF'];
  const usdInr = data?.usd_inr || 84;
  const startDate = data?.latest_month || new Date().toISOString().slice(0, 10);

  const plan = useMemo(() => planned && cagrs ? computeDerived(planned, meta, entities, cagrs) : null, [planned, cagrs, meta, entities]);
  const actual = useMemo(() => data?.matrix && cagrs ? computeDerived(data.matrix, meta, entities, cagrs) : null, [data, cagrs, meta, entities]);

  // Forecast window: fixed 5-year slice. Dropdown picks WHICH 5-year window from
  // the current month forward (offset 0 = current..+5yr, 5 = +5..+10yr, etc).
  // Persisted in fcPrefs.planFcOffset (years).
  const FORECAST_WINDOW_YEARS = 5;
  const [forecastOffset, setForecastOffset] = useState(() => Number(fcPrefs.planFcOffset) || 0);
  const setForecastOffsetPersist = v => { setForecastOffset(v); saveFcPrefs?.({ planFcOffset: v }); };

  // Build enough rows to cover offset + window, then slice when rendering.
  const forecast = useMemo(() => {
    if (!plan) return [];
    return buildForecast({ startDate, opening: plan.networth, monthlyBase, monthlySchedule, annualCagr: plan.blendedCagr, months: (forecastOffset + FORECAST_WINDOW_YEARS) * 12, overrides, flows });
  }, [plan, monthlyBase, monthlySchedule, startDate, overrides, flows, forecastOffset]);
  const forecastWindow = useMemo(() => forecast.slice(forecastOffset * 12), [forecast, forecastOffset]);

  // Window label helpers
  const monthYearLabel = ymd => {
    const d = new Date(ymd + 'T00:00:00Z');
    return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  };
  const addYears = (ymd, yrs) => {
    const d = new Date(ymd + 'T00:00:00Z');
    d.setUTCFullYear(d.getUTCFullYear() + yrs);
    return d.toISOString().slice(0, 10);
  };
  // Dropdown options: 6 windows = 30 years of horizon. Adjust if needed.
  const forecastOptions = [0, 5, 10, 15, 20, 25].map(off => ({
    off,
    label: `${monthYearLabel(addYears(startDate, off))} – ${monthYearLabel(addYears(startDate, off + FORECAST_WINDOW_YEARS))}`,
  }));

  // DOB-driven milestones for the primary holder + a long projection to reach them
  const milestones = useMemo(() => milestonesFor(data?.dob), [data]);
  const milestoneForecast = useMemo(() => {
    if (!plan) return [];
    return buildForecast({ startDate, opening: plan.networth, monthlyBase, monthlySchedule, annualCagr: plan.blendedCagr, months: 480, overrides, flows });
  }, [plan, monthlyBase, monthlySchedule, startDate, overrides, flows]);

  if (!data || !plan || !actual) {
    return <div className="flex items-center justify-center h-64" style={{ color: 'var(--text-dim)', fontSize: 16 }}>Loading…</div>;
  }

  const startEdit = (key, val) => { setEdit(key); setEditVal(String(val)); };
  const commitCell = (e, a) => { const n = parseFloat(editVal); if (!isNaN(n) && n >= 0) setPlanned(p => ({ ...p, [e]: { ...p[e], [a]: n } })); setEdit(null); };
  const commitCagr = a => { const n = parseFloat(editVal); if (!isNaN(n) && n >= 0) setCagrs(c => ({ ...c, [a]: n / 100 })); setEdit(null); };
  const commitOverride = date => { const n = parseFloat(editVal); if (!isNaN(n) && n >= 0) setOverrides(o => ({ ...o, [date]: n })); setEdit(null); };
  const commitFlow = date => { const n = parseFloat(editVal); if (!isNaN(n)) setFlows(f => ({ ...f, [date]: n })); setEdit(null); };
  const commitMonthly = date => { const n = parseFloat(editVal); if (!isNaN(n) && n >= 0) setMonthlySchedule(s => ({ ...s, [date]: n })); setEdit(null); };

  const reset = () => {
    setPlanned(JSON.parse(JSON.stringify(data.matrix)));
    const c = {}; meta.forEach(m => { c[m.asset] = m.cagr; }); setCagrs(c);
    setOverrides({}); setFlows({}); setMonthlySchedule({});
  };
  // baseline = the saved plan if one exists, else current actuals/defaults
  const hasSaved = savedPlan?.planned && Object.keys(savedPlan.planned).length;
  const basePlanned = hasSaved ? savedPlan.planned : data.matrix;
  const baseCagr = a => (savedPlan?.cagrs?.[a] ?? meta.find(m => m.asset === a)?.cagr);
  const dirty = JSON.stringify(planned) !== JSON.stringify(basePlanned)
    || meta.some(m => cagrs[m.asset] !== baseCagr(m.asset))
    || monthlyBase !== (savedPlan?.monthlyBase ?? defaultMonthly)
    || JSON.stringify(monthlySchedule) !== JSON.stringify(savedPlan?.monthlySchedule || {})
    || JSON.stringify(overrides) !== JSON.stringify(savedPlan?.overrides || {})
    || JSON.stringify(flows) !== JSON.stringify(savedPlan?.flows || {});

  const savePlan = async () => {
    setSaveState('saving');
    // Snapshot the actuals matrix at save time. Used by the buy/sell engine to
    // distinguish cells the user INTENTIONALLY changed (planned !== baseline)
    // from cells left untouched (planned === baseline) so market drift on
    // untouched assets does not produce phantom rebalance signals.
    const baseline = JSON.parse(JSON.stringify(data?.matrix || {}));
    const payload = { planned, cagrs, monthlyBase, monthlySchedule, overrides, flows, baseline };
    try {
      const r = await apiSend('/plan', { plan: payload });
      const j = await r.json();
      if (j.ok) { onSaved?.(payload, j.updated_at); setSaveState('saved'); setTimeout(() => setSaveState('idle'), 2000); }
      else setSaveState('idle');
    } catch { setSaveState('idle'); }
  };

  const milestoneCorpus = ms => {
    const row = milestoneForecast.find(r => milestoneAt(r.date, [ms]));
    return row ? row.closing : null;
  };

  const memberActual = a => member === 'ALL' ? actual.assetTotals[a] : (data.matrix[member]?.[a] || 0);
  const memberPlanned = a => member === 'ALL' ? plan.assetTotals[a] : (planned[member]?.[a] || 0);
  const memberActualNW = member === 'ALL' ? actual.networth : actual.entityTotals[member];
  const memberPlannedNW = member === 'ALL' ? plan.networth : plan.entityTotals[member];
  const netFunding = memberPlannedNW - memberActualNW;

  // small delta line for a planned cell vs actual
  const driftTH = 25000; // flat ₹25k floor — shows real per-cell trades, ignores FX jitter
  const cellDelta = (e, a) => {
    const d = (planned[e]?.[a] || 0) - (data.matrix[e]?.[a] || 0);
    if (Math.abs(d) < driftTH) return null;
    return <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginTop: '2px', color: d > 0 ? 'var(--pos)' : 'var(--neg)' }}>
      {d > 0 ? `▲ Buy ${fmt(d)}` : `▼ Sell ${fmt(-d)}`}
    </span>;
  };

  return (
    <div className="min-h-screen px-4 lg:px-8 py-6 lg:py-8" style={pageBg}>
      {/* Header */}
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-display" style={{ color: 'var(--text)' }}>Planning</h1>
          <p className="text-base mt-1" style={{ color: 'var(--text-dim)' }}>Plan target allocation — buy/sell, CAGR & forecast recompute live</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPlanned(JSON.parse(JSON.stringify(data.matrix)))}
            title="Pull the latest Actuals into the planned cells. Keeps overrides, flows, monthly schedule, and CAGRs untouched."
            className="text-base px-5 py-2.5 rounded-xl"
            style={{ color: 'var(--text-dim)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
            Sync from Actuals
          </button>
          {dirty ? (
            <button onClick={reset} className="text-base px-5 py-2.5 rounded-xl" style={{ color: 'var(--neg)', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)' }}>
              Reset to actuals
            </button>
          ) : null}
          <button onClick={savePlan} disabled={saveState === 'saving'}
            className="flex items-center gap-2 text-base font-semibold px-5 py-2.5 rounded-xl transition-all active:scale-95"
            style={{ background: saveState === 'saved' ? 'rgba(52,211,153,0.2)' : 'var(--accent-grad)', color: saveState === 'saved' ? 'var(--pos)' : '#fff', boxShadow: saveState === 'saved' ? 'none' : '0 4px 16px rgba(249,115,22,0.3)' }}>
            {saveState === 'saved' ? <><Check size={17} /> Saved</> : <><Save size={17} /> {saveState === 'saving' ? 'Saving…' : 'Finalise & Save Plan'}</>}
          </button>
        </div>
      </div>

      {/* Drift reminder vs saved plan */}
      <PlanReminder actualMatrix={data.matrix} savedPlan={savedPlan} meta={meta} entities={entities} savedAt={savedAt} />

      {/* ===== Planned Allocation (editable, per-cell buy/sell) ===== */}
      <div style={{ ...glassCard, padding: 0, overflow: 'hidden', marginBottom: '22px' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={sectionTitle}>Planned Allocation</span>
          <span style={sectionSub}>click any ₹ cell or CAGR % — buy/sell shown vs your actual</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
            <thead>
              <tr style={{ background: 'var(--head-bg)' }}>
                <th style={{ ...th, textAlign: 'left' }}>Member</th>
                {meta.map(m => <th key={m.asset} style={th}>{m.label}</th>)}
                <th style={{ ...th, color: '#fb923c' }}>Networth</th>
              </tr>
              <tr style={{ background: 'var(--head-2)' }}>
                <td style={{ ...labelCell, color: 'var(--text-mute)', fontSize: '13px' }}>Asset CAGR</td>
                {meta.map(m => {
                  const k = `cagr|${m.asset}`; const c = cagrs[m.asset];
                  return (
                    <td key={m.asset} style={{ ...cell, padding: '6px 14px' }}>
                      {edit === k ? (
                        <input autoFocus type="number" step="0.1" value={editVal} style={{ ...editInput, width: '74px' }}
                          onChange={e => setEditVal(e.target.value)} onBlur={() => commitCagr(m.asset)}
                          onKeyDown={e => { if (e.key === 'Enter') commitCagr(m.asset); if (e.key === 'Escape') setEdit(null); }} />
                      ) : (
                        <span onClick={() => startEdit(k, (c * 100).toFixed(1))} style={{ cursor: 'pointer', color: c !== m.cagr ? '#fb923c' : 'var(--pos)', fontSize: '14px', borderBottom: '1px dashed rgba(255,255,255,0.18)' }}>
                          {(c * 100).toFixed(1)}%
                        </span>
                      )}
                    </td>
                  );
                })}
                <td style={cell}></td>
              </tr>
              <tr style={{ background: 'var(--head-2)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <td style={{ ...labelCell, color: 'var(--text-mute)', fontSize: '13px' }}>Class</td>
                {meta.map(m => <td key={m.asset} style={{ ...cell, color: CLASS_COLOR[m.class], fontSize: '13px' }}>{m.class}</td>)}
                <td style={cell}></td>
              </tr>
            </thead>
            <tbody>
              {entities.map(e => (
                <tr key={e} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ ...labelCell, color: 'var(--text)', fontWeight: 600 }}>{memberLabel(e, members)}</td>
                  {meta.map(m => {
                    const key = `${e}|${m.asset}`; const val = planned[e]?.[m.asset] || 0;
                    const changed = val !== (data.matrix[e]?.[m.asset] || 0);
                    return (
                      <td key={m.asset} style={{ ...cell, padding: '8px 14px', verticalAlign: 'top' }}>
                        {edit === key ? (
                          <input autoFocus type="number" value={editVal} style={{ ...editInput, width: '108px' }}
                            onChange={ev => setEditVal(ev.target.value)} onBlur={() => commitCell(e, m.asset)}
                            onKeyDown={ev => { if (ev.key === 'Enter') commitCell(e, m.asset); if (ev.key === 'Escape') setEdit(null); }} />
                        ) : (
                          <>
                            <span onClick={() => startEdit(key, Math.round(val))} style={{ cursor: 'pointer', color: changed ? '#fb923c' : (val ? 'var(--text)' : 'var(--text-mute)'), borderBottom: '1px dashed rgba(255,255,255,0.18)' }}>
                              {val ? fmt(val) : '–'}
                            </span>
                            {cellDelta(e, m.asset)}
                          </>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ ...cell, color: '#fb923c', fontWeight: 700, verticalAlign: 'top' }}>{fmt(plan.entityTotals[e])}</td>
                </tr>
              ))}
              <tr style={{ background: 'rgba(249,115,22,0.07)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <td style={{ ...labelCell, color: '#fb923c', fontWeight: 700 }}>TOTAL</td>
                {meta.map(m => <td key={m.asset} style={{ ...cell, color: 'var(--text)', fontWeight: 600 }}>{fmt(plan.assetTotals[m.asset])}</td>)}
                <td style={{ ...cell, color: '#fb923c', fontWeight: 700 }}>{fmt(plan.networth)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-end gap-8 px-6 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Planned CAGR</p>
            <p className="font-display" style={{ fontSize: '30px', fontWeight: 700, color: 'var(--pos)' }}>{(plan.blendedCagr * 100).toFixed(2)}%</p>
          </div>
          {['EQUITY', 'DEBT', 'CASH'].map(c => (
            <div key={c}>
              <p style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c}</p>
              <p className="font-display" style={{ fontSize: '22px', fontWeight: 700, color: CLASS_COLOR[c] }}>{plan.classPct[c].toFixed(1)}%</p>
            </div>
          ))}
          {/* Net funding advice — to move actuals onto this plan (per-cell buy/sell shown in the table above) */}
          <div className="ml-auto text-right">
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>To reach this plan</p>
            <p className="font-display" style={{ fontSize: '22px', fontWeight: 700, color: Math.abs(netFunding) < driftTH ? 'var(--pos)' : '#fbbf24' }}>
              {Math.abs(netFunding) < driftTH ? 'Balanced — rebalance only' : (netFunding > 0 ? `Add ${fmt(netFunding)} new` : `Free up ${fmt(-netFunding)}`)}
            </p>
          </div>
        </div>
      </div>

      {/* Year-selectable forecast cards */}
      <div className="flex flex-wrap gap-4 mb-6">
        {(() => {
          const startYear = Number(startDate.slice(0, 4));
          const colors = ['#fb923c', '#f97316', '#ea580c'];
          const setYear = (i, fn) => setCardYears(ys => { const next = ys.map((y, j) => j === i ? Math.max(startYear, fn(y)) : y); saveFcPrefs?.({ planYears: next }); return next; });
          return cardYears.map((yr, i) => {
            const row = milestoneForecast.find(r => { const d = new Date(r.date + 'T00:00:00Z'); return d.getUTCFullYear() === yr && d.getUTCMonth() === 11; });
            const v = row ? row.closing : null;
            const ageY = data?.dob ? ageAt(data.dob, `${yr}-12-31`) : null;
            return (
              <div key={i} style={{ ...glassCard, padding: '18px 22px', flex: '1 1 200px' }}>
                <p style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }} className="flex items-center gap-2">
                  Forecast in
                  <span className="inline-flex items-center gap-1">
                    <button onClick={() => setYear(i, y => y - 1)} style={{ color: 'var(--accent)', fontWeight: 700 }}>‹</button>
                    <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{yr}</span>
                    <button onClick={() => setYear(i, y => y + 1)} style={{ color: 'var(--accent)', fontWeight: 700 }}>›</button>
                  </span>
                  {ageY != null && <span style={{ color: 'var(--text-mute)' }}>· age {ageY}</span>}
                </p>
                <p className="font-display" style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text)' }}>{v ? fmt(v) : '—'}</p>
                <p style={{ fontSize: '15px', fontWeight: 600, color: colors[i % colors.length], marginTop: '2px' }}>{v ? fmtUSD(v, usdInr) : ''}</p>
              </div>
            );
          });
        })()}
      </div>

      {/* ===== Forecast ===== */}
      <div style={{ ...glassCard, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <span style={sectionTitle}>Monthly Forecast</span>
            <span style={sectionSub}>@ {(plan.blendedCagr * 100).toFixed(2)}% CAGR · base +{fmt(monthlyBase)}/mo · edit cells to cascade</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Period</span>
            <select value={forecastOffset} onChange={e => setForecastOffsetPersist(Number(e.target.value))}
              className="px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', color: 'var(--text)', colorScheme: 'dark', minWidth: '180px' }}>
              {forecastOptions.map(o => <option key={o.off} value={o.off}>{o.label}</option>)}
            </select>
            <span style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Monthly Investment</span>
            <input type="number" value={monthlyBase} onChange={e => {
                const v = Math.max(0, Number(e.target.value) || 0);
                setMonthlyBase(v);
                // Cascade: clear per-month overrides for the current month and beyond so the
                // new base applies forward. Past months keep their historical edits.
                setMonthlySchedule(s => {
                  const next = {};
                  for (const k of Object.keys(s)) if (k < startDate) next[k] = s[k];
                  return next;
                });
              }}
              className="px-3 py-2 rounded-lg text-right text-sm outline-none"
              style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', color: 'var(--text)', fontFamily: 'monospace', width: '150px', colorScheme: 'dark' }} />
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '780px' }}>
            <thead>
              <tr style={{ background: 'var(--head-bg)' }}>
                <th style={{ ...th, textAlign: 'left' }}>Date</th><th style={th}>Opening</th><th style={th}>Monthly Inv.</th><th style={th}>Interest</th><th style={th}>Withdraw / Deposit</th><th style={th}>Closing</th><th style={th}>Closing (USD)</th>
              </tr>
            </thead>
            <tbody>
              {forecastWindow.map((row, i) => {
                const ms = milestoneAt(row.date, milestones);
                const oKey = `open|${row.date}`, fKey = `flow|${row.date}`, mKey = `mon|${row.date}`;
                const hasO = overrides[row.date] !== undefined, hasF = (flows[row.date] || 0) !== 0, hasM = monthlySchedule[row.date] !== undefined;
                return (
                  <tr key={row.date} style={{ background: ms ? 'rgba(249,115,22,0.07)' : (i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'), borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                    <td style={{ ...labelCell, color: ms ? '#fb923c' : 'var(--text-dim)', fontWeight: ms ? 700 : 400, whiteSpace: 'nowrap', fontSize: '15px' }}>
                      {row.label}{ms ? ` · ${ms.label}` : ''}
                    </td>
                    <td style={{ ...cell, padding: '8px 14px' }}>
                      {edit === oKey ? (
                        <input autoFocus type="number" value={editVal} style={{ ...editInput, width: '116px' }}
                          onChange={e => setEditVal(e.target.value)} onBlur={() => commitOverride(row.date)}
                          onKeyDown={e => { if (e.key === 'Enter') commitOverride(row.date); if (e.key === 'Escape') { setOverrides(o => { const n = { ...o }; delete n[row.date]; return n; }); setEdit(null); } }} />
                      ) : (
                        <span onClick={() => startEdit(oKey, Math.round(row.opening))} title="Click to set actual opening"
                          style={{ cursor: 'pointer', color: hasO ? 'var(--pos)' : 'var(--text-dim)', borderBottom: '1px dashed rgba(255,255,255,0.14)' }}>{fmt(row.opening)}</span>
                      )}
                    </td>
                    {/* editable monthly investment (cascades forward) */}
                    <td style={{ ...cell, padding: '8px 14px' }}>
                      {edit === mKey ? (
                        <input autoFocus type="number" value={editVal} style={{ ...editInput, width: '108px' }}
                          onChange={e => setEditVal(e.target.value)} onBlur={() => commitMonthly(row.date)}
                          onKeyDown={e => { if (e.key === 'Enter') commitMonthly(row.date); if (e.key === 'Escape') { setMonthlySchedule(s => { const n = { ...s }; delete n[row.date]; return n; }); setEdit(null); } }} />
                      ) : (
                        <span onClick={() => startEdit(mKey, Math.round(row.monthly))} title="Set monthly investment from here on"
                          style={{ cursor: 'pointer', color: hasM ? '#fb923c' : 'var(--text-dim)', borderBottom: '1px dashed rgba(255,255,255,0.14)' }}>{fmt(row.monthly)}</span>
                      )}
                    </td>
                    <td style={{ ...cell, color: 'var(--pos)' }}>{fmt(row.interest)}</td>
                    <td style={{ ...cell, padding: '8px 14px' }}>
                      {edit === fKey ? (
                        <input autoFocus type="number" value={editVal} placeholder="±" style={{ ...editInput, width: '108px' }}
                          onChange={e => setEditVal(e.target.value)} onBlur={() => commitFlow(row.date)}
                          onKeyDown={e => { if (e.key === 'Enter') commitFlow(row.date); if (e.key === 'Escape') { setFlows(f => { const n = { ...f }; delete n[row.date]; return n; }); setEdit(null); } }} />
                      ) : (
                        <span onClick={() => startEdit(fKey, flows[row.date] || '')} title="– withdraw / + deposit"
                          style={{ cursor: 'pointer', color: hasF ? (row.flow > 0 ? 'var(--pos)' : 'var(--neg)') : 'var(--text-mute)', borderBottom: '1px dashed rgba(255,255,255,0.14)' }}>
                          {hasF ? (row.flow > 0 ? `+${fmt(row.flow)}` : `${fmt(row.flow)}`) : '+ / –'}
                        </span>
                      )}
                    </td>
                    <td style={{ ...cell, color: 'var(--text)', fontWeight: 700 }}>{fmt(row.closing)}</td>
                    <td style={{ ...cell, color: '#fb923c' }}>{fmtUSD(row.closing, usdInr)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
